import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";

import { ApiError, api } from "../api/client";

/**
 * Remove a document from use. Two-step: the first click asks to confirm in
 * place, the second calls DELETE /api/documents/{id}. The removal is
 * reversible on the server (the document is hidden, not wiped), but staff
 * stop seeing it straight away and it can no longer be signed.
 *
 * Render only when the list API said `can_remove` (per-category Delete
 * permission, never for signed documents) — the server enforces it again.
 */
export default function RemoveDocumentButton({
  documentId,
  title,
  onRemoved,
  compact = false,
}: {
  documentId: number;
  title: string;
  onRemoved: () => void;
  compact?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const size = compact ? "px-2 py-1" : "px-3 py-1.5";

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete(`/api/documents/${documentId}`);
      onRemoved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't remove the document");
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className={`inline-flex items-center gap-1 rounded-lg border border-red-200 ${size} text-xs font-medium text-red-700 hover:bg-red-50`}
        title={`Remove "${title}"`}
      >
        <Trash2 size={12} aria-hidden /> Remove
      </button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 rounded-lg bg-red-50 px-2 py-1 text-xs text-red-800">
      <span>Remove this document? Staff will no longer see or sign it.</span>
      <button
        type="button"
        onClick={() => void remove()}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-0.5 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
      >
        {busy && <Loader2 size={11} className="animate-spin" aria-hidden />}
        Yes, remove
      </button>
      <button
        type="button"
        onClick={() => {
          setConfirming(false);
          setError("");
        }}
        disabled={busy}
        className="rounded-md px-2 py-0.5 font-medium text-red-800 hover:underline"
      >
        Cancel
      </button>
      {error && <span className="basis-full text-red-700">{error}</span>}
    </span>
  );
}
